import Papa from 'papaparse';
import {
  SearchableNewspaperOrderRecord,
  SearchableNoticeRecord
} from 'lib/types/searchable';
import { FromElasticRecordCsvColumn } from '../constants/csvColumns';

type DataAndColumns<
  T extends SearchableNewspaperOrderRecord | SearchableNoticeRecord
> = {
  data: T[];
  columns: FromElasticRecordCsvColumn<T>[];
};

/**
 * Exports an array of Elastic SearchableRecords to CSV using the provided columns and acessor keys / accessor functionsand triggers a download.
 * @param data The array of Elastic SearchableRecords to export
 * @param columns The column definitions (header, accessorKey or accessorFn) for the CSV
 * @param filename The filename for the downloaded CSV
 */
export function exportSearchableRecordsToCSV({
  dataAndColumns,
  filename
}: {
  dataAndColumns: Array<
    | DataAndColumns<SearchableNewspaperOrderRecord>
    | DataAndColumns<SearchableNoticeRecord>
  >;
  filename: string;
}) {
  if (dataAndColumns.length === 0) {
    throw new Error('At least one data-column pair must be provided');
  }

  const allCsvRows: Record<string, string | number>[] = [];

  dataAndColumns.forEach(pair => {
    const { data, columns } = pair;
    const csvRows = data.map(record => {
      const csvRow: Record<string, string | number> = {};

      columns.forEach(column => {
        if ('accessorFn' in column) {
          csvRow[column.header] = column.accessorFn(record as any);
        } else {
          const value = (record as any)[column.accessorKey];
          csvRow[column.header] = value != null ? String(value) : '';
        }
      });

      return csvRow;
    });

    allCsvRows.push(...csvRows);
  });

  const csv = Papa.unparse(allCsvRows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
}
